const getGeometryProp = (geometry = {}, prop) => {
  const location = geometry.location || {};
  const val = location[prop];

  if (!val) { return 0; }

  return typeof val === `function`
    ? val()
    : val;
};

const parseAddressComponents = (place = {}) => {
  const lat = getGeometryProp(place.geometry, `lat`);
  const lng = getGeometryProp(place.geometry, `lng`);
  let address1 = ``;
  let postcode = ``;
  let city = ``;
  let province = ``;
  let country = ``;
  let streetNumber = ``;

  if (place.address_components) {
    for (const component of place.address_components) {
      const types = component.types || [];

      if (types.includes("street_number")) {
        streetNumber = component.long_name;
        address1 = `${streetNumber} ${address1}`;
        continue;
      }

      if (types.includes("route")) {
        address1 += component.short_name;
        continue;
      }

      if (types.includes("postal_code")) {
        postcode = `${component.long_name}${postcode}`;
        continue;
      }

      if (types.includes("postal_code_suffix")) {
        postcode = `${postcode}-${component.long_name}`;
        continue;
      }

      // Prefer borough-style city (sublocality_level_1) when available, otherwise locality
      if (types.includes("sublocality_level_1")) {
        city = component.long_name;
        continue;
      }

      if (types.includes("locality")) {
        // Only set locality if we don't already have a borough-style city
        if (!city) {
          city = component.long_name;
        }
        continue;
      }

      if (types.includes("administrative_area_level_1")) {
        province = component.short_name;
        continue;
      }

      if (types.includes("country")) {
        country = component.short_name;
      }
    }
  }

  return {
    place_id: place.place_id,
    city,
    province,
    address1,
    streetNumber,
    postcode,
    country,
    lat,
    lng,
  };
};


if (!customElements.get('location-modal-opener')) {
  customElements.define('location-modal-opener', class LocationModalOpener extends HTMLElement {
    constructor() {
      super();

      this.locationModal = document.getElementById('LocationModal');
      this.querySelector('.location-modal-opener__header-button').addEventListener('click', this.openModal.bind(this));

      this.updateLocationUI();
      subscribe(PUB_SUB_EVENTS.deliveryLocationUpdate, this.updateLocationUI.bind(this));
    }

    openModal() {
      this.locationModal.showModal();
    }


    updateLocationUI() {
      const deliveryLocation = JSON.parse(window.localStorage.getItem('delivery_location'));
      this.querySelectorAll('[data-delivery-location-address]').forEach((el) => {
        el.textContent = deliveryLocation ? deliveryLocation.address[el.dataset.deliveryLocationAddress]
          : window.gopuffShopify.strings.enter_address;
      });
    }
  });

}

if (!customElements.get('location-modal')) {
  customElements.define('location-modal', class LocationModal extends ModalDialog {
    constructor() {
      super();

      
      this.autocompleteInitialized = false;
      this.autocompleteField = this.querySelector('#LocationModal-custom-autocomplete');
      this.autocompleteInput = this.querySelector('#LocationModal-custom-autocomplete-input');
      this.confirmLocation = this.querySelector(".location-modal__confirm-location");
      this.changeLocation = this.querySelector('.location-modal__change-address');
      this.changeLocation.addEventListener('click', this.handleChangeLocation.bind(this));
      this.mapElement = this.querySelector('.location-modal__map');
      this.map;
      this.marker;
      this.poweredbyCustomer;
      
      if (!this.confirmAgeVerification()) { return; }

      this.form = this.querySelector('form');
      this.querySelector('.location-modal__toggle').addEventListener('click', this.onCloseModal.bind(this));
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

      this.outOfZoneConfirm = this.form.querySelector('#OutOfZoneConfirm');
      if (this.outOfZoneConfirm) {
        this.outOfZoneConfirm.addEventListener('click', this.handleOutOfZoneConfirm.bind(this));
      }

      this.usePoweredbyCustomer()
        .then(() => {
          const deliveryLocation = window.localStorage.getItem('delivery_location');
          const deliveryLocationDismissed = window.localStorage.getItem('delivery_location_dismissed');
          if (!deliveryLocation) {
            if (!deliveryLocationDismissed) {
              this.showModal();
            }
          }
          else {
            const deliveryLocationObject = JSON.parse(deliveryLocation);
            for (const [key, value] of Object.entries(deliveryLocationObject.address)) {
              if (this.form.querySelector(`[name=${key}]`)) {
                this.form.querySelector(`[name=${key}]`).value = value;
              }
            }

            this.setCategoriesView();
            this.checkDeliveryZoneIsOpen(deliveryLocationObject.delivery_zone);

            if (Cookies.get('_gopuff_recent_delivery_location') == 'true') {
              return;
            }
            this.getDeliveryZone(deliveryLocationObject.address, (response) => {
              if (response.error) {
                console.error(response);
                return;
              }
              this.saveDeliveryLocation(deliveryLocationObject.address, response);
              this.checkDeliveryZoneIsOpen(response);
            });
          }
        })
    }

    async getPlaceForAddress(address = ``, id) {
      if (!id && !address) { return; }
  
      const places = await google.maps.importLibrary("places")
      const service = new places.PlacesService(document.createElement(`div`));
      let place_id = id;
  
      if (!place_id) {
        const placeRequest = {
          query: address,
          fields: [`place_id`],
        };
  
        const place = await new Promise((resolve) => {
          service.findPlaceFromQuery(placeRequest, (results, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK) { return resolve(null); }
  
            return resolve(results[0] || null);
          });
        });
  
        if (!place) { return; }
  
        place_id = place.place_id;
      }
  
      const detailsRequest = {
        placeId: place_id,
        fields: [`address_components`, `geometry`, `formatted_address`, `types`],
      };
  
      const detailedPlace = await new Promise((resolve) => {
        service.getDetails(detailsRequest, (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK) { return resolve(null); }
  
          return resolve(place);
        });
      });
  
      return {
        ...detailedPlace,
        place_id,
      };
    }

    getAgeVerification() {
      if (!window.gopuffShopify.settings.age_verification) {
        return;
      }

      if (window.gopuffShopify.settings.age_verification.cookie_name) {
        return JSON.parse(Cookies.get(window.gopuffShopify.settings.age_verification.cookie_name));
      }

      else {
        return window.gopuffShopify.customer;
      }
    }

    usePoweredbyCustomer() {
      return new Promise(async (resolve) => {
        const addressCookie = Cookies.get(`_poweredby_delivery_address`);
        if ((!window.gopuffShopify.settings.age_verification || !this.poweredbyCustomer) && !addressCookie) { return resolve(false); }

        const successCookieName = `gopuff_is_poweredby_customer_used`;

        if (Cookies.get(successCookieName) === `true`) { return resolve(true); }
  
        if (!this.poweredbyCustomer?.address && !addressCookie) {
          Cookies.set(successCookieName, `true`);
  
          return resolve(true);
        }

        const { address1, unit, city, state, zip, country_code } = addressCookie ? JSON.parse(addressCookie) : this.poweredbyCustomer.address;
        const address = [address1, city, state, zip, country_code].filter(Boolean).join(`, `);
        const place = await this.getPlaceForAddress(address);
    
        if (!place) { return emptyLocation; }
    
        const parsedAddress = parseAddressComponents(place);
        const destination = {
          autocomplete: address,
          place_id: parsedAddress.place_id,
          city: parsedAddress.city,
          province: parsedAddress.province,
          address1: parsedAddress.address1,
          country: parsedAddress.country,
          postalCode: parsedAddress.postcode,
          latitude: String(parsedAddress.lat),
          longitude: String(parsedAddress.lng),
          unit,
        };
  
        this.getDeliveryZone(destination, (response) => {
          if (response.error || (response.id == -1)) {
            console.error(response);
          }
  
          if (response.error) { return resolve(false); }
  
          if (response.id !== -1) {
            this.saveDeliveryLocation(destination, response);
            this.updateLocation();
            this.setCheckoutAddress(destination);
          }
  
          Cookies.set(successCookieName, `true`);
          return resolve(true);
        });
      });
    }

    confirmAgeVerification() {
      if (!window.gopuffShopify.settings.age_verification) { return true; }

      const ageVerification = this.getAgeVerification();

      if (!ageVerification && window.gopuffShopify.settings.age_verification.cookie_name) {
        if (window.gopuffShopify.settings.age_verification.redirect_url) {
          document.location = window.gopuffShopify.settings.age_verification.redirect_url;
        }

        return false;
      }

      this.poweredbyCustomer = ageVerification;

      return true;
    }

    onCloseModal() {
      if (window.localStorage.getItem('delivery_location') == null) {
        window.localStorage.setItem('delivery_location_dismissed', true);
      }
    }

    async placeChanged(providedPlace = null) {
      const place = providedPlace;
      let address1 = "";
      let city = "";
      let province = "";
      let postcode = "";
      let lat, lng;

      if (place && place.address_components) {
        for (const component of place.address_components) {
          const types = component.types || [];

          if (types.includes("street_number")) {
            address1 = `${component.long_name} ${address1}`;
            continue;
          }

          if (types.includes("route")) {
            address1 += component.short_name;
            continue;
          }

          if (types.includes("postal_code")) {
            postcode = `${component.long_name}${postcode}`;
            continue;
          }

          if (types.includes("postal_code_suffix")) {
            postcode = `${postcode}-${component.long_name}`;
            continue;
          }

          // Prefer borough-style city (sublocality_level_1) when available, otherwise locality
          if (types.includes("sublocality_level_1")) {
            city = component.long_name;
            continue;
          }

          if (types.includes("locality")) {
            // Only set locality if we don't already have a borough-style city
            if (!city) {
              city = component.long_name;
            }
            continue;
          }

          if (types.includes("administrative_area_level_1")) {
            province = component.short_name;
            continue;
          }

          if (types.includes("country")) {
            this.form.querySelector('[name=country]').value = component.short_name;
          }
        }
        this.form.querySelector('[name=city]').value = city;
        this.form.querySelector('[name=province]').value = province;

        this.form.querySelector('[name=address1]').value = address1;
        this.form.querySelector('[name=postalCode]').value = postcode;

        lat = place.geometry.location.lat();
        lng = place.geometry.location.lng();

        this.form.querySelector('[name=latitude]').value = lat;
        this.form.querySelector('[name=longitude]').value = lng;
      }
      else {
        address1 = this.form.querySelector('[name=address1]').value;
        city = this.form.querySelector('[name=city]').value;
        province = this.form.querySelector('[name=province]').value;
        postcode = this.form.querySelector('[name=postalCode]').value;

        lat = Number(this.form.querySelector('[name=latitude]').value);
        lng = Number(this.form.querySelector('[name=longitude]').value);

        if (lat == 0 || lng == 0) {
          return;
        }

      }

      this.form.querySelector('.location-modal__address-component1').textContent = address1;
      this.form.querySelector('.location-modal__address-component2').textContent = `${city}, ${province} ${postcode}`;


      if (!this.map) {
        const { Map } = await google.maps.importLibrary("maps");
        this.map = new Map(this.mapElement, {
          center: { lat, lng },
          zoom: 19,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          gestureHandling: "none",
          keyboardShortcuts: false,
        });
        this.marker = new google.maps.Marker({
          position: { lat, lng },
          map: this.map,
        });
      }
      else {
        this.map.setCenter({ lat, lng });
        this.marker.setPosition({ lat, lng });
      }

      this.autocompleteField.classList.add('hidden');
      this.confirmLocation.classList.remove('hidden');
      this.form.querySelector('[name=unit]').focus();
      this.handleErrorMessage(false)
    }

    async initAutocomplete() {
      subscribe(PUB_SUB_EVENTS.customLocationComponentPlaceChanged, (event) => {
        if (event.source === `LocationModal-custom-autocomplete`) {
          this.placeChanged(event.place);
        }
      });

      await this.autocompleteField.initGoogle();

      this.autocompleteInitialized = true;
    }

    async showModal() {
      this.show();

      if (!this.autocompleteInitialized) {
        await this.initAutocomplete();
      }

      if (this.autocompleteInput.value == '') {
        this.autocompleteField.classList.remove('hidden');
        if (!document.querySelector('age-modal[open]')) {
          this.autocompleteInput.focus();
        }
        this.confirmLocation.classList.add('hidden');
      }

      this.placeChanged();
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute('open');
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia();

      const deliveryLocation = JSON.parse(window.localStorage.getItem('delivery_location'));
      if (!deliveryLocation) {
        window.localStorage.setItem('delivery_location_dismissed', true);
      }
    }

    handleChangeLocation(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      this.handleErrorMessage(false);
      this.autocompleteInput.value = '';
      this.form.querySelector('[name=unit]').value = '';
      this.autocompleteField.classList.remove('hidden');
      this.autocompleteInput.focus();
      this.confirmLocation.classList.add('hidden');
    }

    async updateLocation() {
      await this.setCategoriesView();

      const deliveryLocation = JSON.parse(window.localStorage.getItem('delivery_location'));
      publish(PUB_SUB_EVENTS.deliveryLocationUpdate, deliveryLocation);
    }


    async setCategoriesView() {
      if (!window.gopuffShopify.categoriesCheck || !window.gopuffShopify.categoriesCheck.enable_categories_check || window.gopuffShopify.categoriesCheck.categories.length <= 0) { return; }

      const deliveryLocation = JSON.parse(window.localStorage.getItem('delivery_location'));

      if (!deliveryLocation) { return; }
      if (deliveryLocation.delivery_zone.categories) {
        for (const category of deliveryLocation.delivery_zone.categories) {
          document.body.classList.add(`${category.label_handle}-enabled`);
        }

        return;
      }

      const categoriesEnabled = [];

      for (const category of window.gopuffShopify.categoriesCheck.categories) {
        const params = {
          country_code: deliveryLocation.delivery_zone.country_code,
          location_id: deliveryLocation.delivery_zone.id,
          product_ids: category.skus
        };

        try {
          const response = await fetch(`${window.gopuffShopify.settings.api_base_url}/products?${new URLSearchParams(params)}`)
          const { products } = await response.json();

          if (products && products.length > 0 && products.some(product => product.quantity > 0)) {
            categoriesEnabled.push(category);
            document.body.classList.add(`${category.label_handle}-enabled`);
          }
        } catch (e) {
          console.log(`error getting categoriesEnabled from /products`, e);
        }
      }

      deliveryLocation.delivery_zone.categories = categoriesEnabled;
      window.localStorage.setItem('delivery_location', JSON.stringify(deliveryLocation));
    }

    setCheckoutAddress(address) {
      const params = {};
      const checkoutParams = {
        'address1': 'checkout[shipping_address][address1]',
        'unit': 'checkout[shipping_address][address2]',
        'city': 'checkout[shipping_address][city]',
        'province': 'checkout[shipping_address][province]',
        'postalCode': 'checkout[shipping_address][zip]',
        'country': 'checkout[shipping_address][country]',
      }

      for (var key in checkoutParams) {
        params[checkoutParams[key]] = address[key];
      }

      const ageVerification = this.getAgeVerification();

      if (ageVerification) {
        const additionalCheckoutParams = {
          'first_name': 'checkout[shipping_address][first_name]',
          'last_name': 'checkout[shipping_address][last_name]',
          'phone': 'checkout[shipping_address][phone]',
          'email': 'checkout[email]',
        }
  
        for (var key in additionalCheckoutParams) {
          params[additionalCheckoutParams[key]] = ageVerification[key];
        }
      }
        
      return fetch(`/checkout?${new URLSearchParams(params)}`);
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.form.querySelector('.location-modal__error');
      this.errorMessage = this.errorMessage || this.form.querySelector('.location-modal__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    handleOutOfZoneConfirm() {
      const deliveryLocation = JSON.parse(window.localStorage.getItem('delivery_location'));
      window.localStorage.removeItem('delivery_location');
      this.updateLocation();
      this.hide();
      if (deliveryLocation) {
        fetch(`${routes.cart_url}/clear`)
          .then(() => {
            window.location.reload();
          });
      }
    }

    getDeliveryZone(destination, callback) {
      fetch(`${window.gopuffShopify.settings.api_base_url}/delivery-zone?shop_domain=${Shopify.shop}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destination: destination }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (!response.error && response.id !== -1 && window.gopuffShopify.settings.out_of_zone_location_ids) {
            const outOfZoneLocationIds = window.gopuffShopify.settings.out_of_zone_location_ids.split(',');
            if (outOfZoneLocationIds.includes(response.id.toString())) {
              response.id = -1;
            }
          }
          callback(response);
        })
        .catch((e) => {
          this.handleErrorMessage(window.gopuffShopify.strings.delivery_zone_request_error);
          console.error(e);
        });
    }

    checkDeliveryZoneIsOpen(response) {
      const isOpen = response.open;
      if (!isOpen) {
        window.gopuffShopify.cart.checkout_disabled = true;

        let message = window.gopuffShopify.strings.closed_message || response.message.description;
        if (response.out_of_service) {
          message = window.gopuffShopify.strings.out_of_service_message || response.message.description;
        }
        window.gopuffShopify.cart.checkout_disabled_message = message;

        const deliveryEstimateMessageEl = document.querySelector('.delivery-estimate__message');
        if (deliveryEstimateMessageEl) {
          deliveryEstimateMessageEl.innerHTML = message;

          const link = response.out_of_service ? window.gopuffShopify.strings.out_of_service_link : window.gopuffShopify.strings.closed_link;
          if (link) {
            const deliveryEstimateEl = deliveryEstimateMessageEl.parentNode;
            if (deliveryEstimateEl.tagName == 'DIV') {
              const linkEl = document.createElement('a');
              linkEl.setAttribute('href', link);
              linkEl.setAttribute('class', 'delivery-estimate__link link link--text focus-inset');
              linkEl.appendChild(deliveryEstimateMessageEl);
              deliveryEstimateEl.appendChild(linkEl);
            }
            else if (deliveryEstimateEl.tagName == 'A') {
              deliveryEstimateEl.setAttribute('href', link);
            }
          }
        }

        const cartItems = document.querySelector('cart-items');
        if (cartItems && cartItems.checkCheckoutDisabled) {
          cartItems.checkCheckoutDisabled();
        }
      }
    }

    saveDeliveryLocation(address, delivery_zone) {
      const deliveryLocationObject = {
        address,
        delivery_zone,
        location_id: delivery_zone.id,
      };
      window.localStorage.removeItem('delivery_location_dismissed');
      window.localStorage.setItem('delivery_location', JSON.stringify(deliveryLocationObject));

      Cookies.set('_gopuff_recent_delivery_location', true, { expires: 1 / 24 / 60 * 5 });
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      evt.stopPropagation();

      const formData = new FormData(this.form);
      const formDataObject = Object.fromEntries(formData);

      this.getDeliveryZone(formDataObject, (response) => {
        if (response.error) {
          this.handleErrorMessage(response.error_message);
          console.error(response);
          return;
        }
        if (response.id == -1) {
          this.handleErrorMessage(window.gopuffShopify.strings.out_of_zone_message);
          return;
        }
        this.handleErrorMessage(false);
        this.saveDeliveryLocation(formDataObject, response);
        this.hide();
        this.updateLocation();
        this.setCheckoutAddress(formDataObject);
        if (window.gopuffShopify.cart.checkout_disabled) {
          window.location.reload();
        }
        this.checkDeliveryZoneIsOpen(response);
      });
    }
  });
}
